import 'maplibre-gl/dist/maplibre-gl.css';

import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import type { Layer } from 'leaflet';
import type { LayerSpecification, StyleSpecification } from 'maplibre-gl';

/**
 * OpenFreeMap dark vector style — free, keyless, no usage limits, commercial use allowed.
 * Replaced CARTO `dark_all` raster tiles, which now require an API key and are being retired.
 */
export const BASEMAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/dark';

// Required wording per openfreemap.org: "OpenFreeMap © OpenMapTiles Data from OpenStreetMap"
export const BASEMAP_ATTRIBUTION =
  '<a href="https://openfreemap.org" target="_blank" rel="noopener">OpenFreeMap</a> &copy; <a href="https://www.openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> Data from <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

// ─── Label tuning ──────────────────────────────────────────────────────────────
// OpenFreeMap's "dark" is CARTO's Dark Matter design, but as shipped every place label
// shares one grey + one font and has no minzoom, so provinces/villages/suburbs compete
// with country names and the client-side collision engine drops labels unevenly.
// We fetch the style once and apply a clear hierarchy before handing it to MapLibre.

/** Lowest zoom at which a label layer may appear (layer ids from the OpenFreeMap dark style). */
const LABEL_MINZOOM: Record<string, number> = {
  place_state: 7,
  place_city: 6,
  place_town: 8,
  place_village: 10,
  place_suburb: 12,
  place_other: 12, // hamlets, isolated dwellings, neighbourhoods
  highway_name_motorway: 10,
  highway_name_other: 14,
};

const COUNTRY_LAYERS = new Set(['place_country_major', 'place_country_minor', 'place_country_other']);

/** Highest zoom at which country / province names stay visible (style default: 6–8 / 12). */
const COUNTRY_MAXZOOM = 10;
const STATE_MAXZOOM = 10;

const COUNTRY_TEXT_COLOR = 'rgb(150,150,150)';
const STATE_TEXT_COLOR = 'rgb(75,75,75)';

export function tuneBasemapStyle(style: StyleSpecification): StyleSpecification {
  const layers = style.layers.map((layer): LayerSpecification => {
    if (layer.type !== 'symbol') return layer;

    const tuned = { ...layer, layout: { ...layer.layout }, paint: { ...layer.paint } };
    const layout = tuned.layout as Record<string, unknown>;
    const paint = tuned.paint as Record<string, unknown>;

    const minzoom = LABEL_MINZOOM[layer.id];
    if (minzoom !== undefined) tuned.minzoom = Math.max(layer.minzoom ?? 0, minzoom);

    if (COUNTRY_LAYERS.has(layer.id)) {
      layout['text-font'] = ['Noto Sans Bold'];
      layout['text-letter-spacing'] = 0.1;
      paint['text-color'] = COUNTRY_TEXT_COLOR;
      tuned.maxzoom = COUNTRY_MAXZOOM;
    } else if (layer.id === 'place_state') {
      paint['text-color'] = STATE_TEXT_COLOR;
      tuned.maxzoom = STATE_MAXZOOM;
    }

    // Place layers reference a `circle-11` sprite that OpenFreeMap doesn't ship — drop it
    // so MapLibre doesn't warn "Image circle-11 could not be loaded" for every tile.
    if (layer.id.startsWith('place_')) delete layout['icon-image'];

    return tuned as LayerSpecification;
  });

  return { ...style, layers };
}

let rawStylePromise: Promise<StyleSpecification> | null = null;

/** Fetches the style JSON once per page and returns a tuned copy; falls back to the plain URL on failure. */
function loadBasemapStyle(): Promise<StyleSpecification | string> {
  rawStylePromise ??= fetch(BASEMAP_STYLE_URL).then((res) => {
    if (!res.ok) throw new Error(`basemap style HTTP ${res.status}`);
    return res.json() as Promise<StyleSpecification>;
  });
  return rawStylePromise
    .then((raw) => tuneBasemapStyle(structuredClone(raw)))
    .catch(() => {
      rawStylePromise = null; // let the next map retry
      return BASEMAP_STYLE_URL;
    });
}

/**
 * Dark vector basemap rendered by MapLibre GL inside a Leaflet map.
 * Drop-in replacement for a basemap `<TileLayer>`: render it as the first child of `<MapContainer>`.
 * Leaflet keeps handling pan/zoom and all overlays; MapLibre only paints the (non-interactive) basemap.
 *
 * NOTE: `maplibre-gl` is intentionally pinned to v5. v6 ships its tile-parsing web worker as a separate
 * `maplibre-gl-worker.mjs` resolved from `import.meta.url`, which Turbopack rewrites to a non-http URL,
 * so the worker never starts and only the style's black background renders. v5 inlines the worker as a
 * blob and needs no bundler plumbing. Upgrading to v6 requires serving the worker + shared chunk from
 * `public/` and calling `setWorkerUrl()` before the first map is created.
 */
export default function BaseMapLayer() {
  const map = useMap();

  useEffect(() => {
    let layer: Layer | null = null;
    let cancelled = false;

    // Lazy import: maplibre-gl touches `window` at module-eval time.
    Promise.all([import('@maplibre/maplibre-gl-leaflet'), loadBasemapStyle()]).then(([{ maplibreGL }, style]) => {
      if (cancelled) return;
      layer = maplibreGL({
        style,
        // The plugin forwards `customAttribution` to Leaflet's attribution control.
        attributionControl: { customAttribution: BASEMAP_ATTRIBUTION },
      });
      layer.addTo(map);
    });

    return () => {
      cancelled = true;
      if (layer) {
        map.removeLayer(layer);
        layer = null;
      }
    };
  }, [map]);

  return null;
}
